import { Inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { CheckoutFacade } from '@checkout/data-access';
import {
  CHECKOUT_CONFIG,
  CheckoutConfig,
  CheckoutRoutes,
  CmsCardPage,
  WORLDPAY_CONFIG,
  WorldpayConfig,
  WorldpayForm,
  WorldpayFormField,
  WorldpayFormValue,
  WorldpayInstance,
  WorldpayIntegrationType,
  WorldpayRedirectRequest,
  WorldpayRedirectResponse,
  WorldpayResult,
  WorldpayStatus,
} from '@checkout/util-models';
import { BasketFacade, QuoteDetails } from '@domgen/dgx-fe-basket';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import {
  CustomerDetails,
  DocumentSendMethod,
  filterNullUndefined,
  mapToLatestFrom,
} from '@domgen/dgx-fe-common';
import { controlValue, formValue } from '@domgen/dgx-fe-forms/shared';
import { ComponentStore } from '@ngrx/component-store';
import {
  Observable,
  combineLatest,
  filter,
  first,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { WorldpayFormConfig } from '../../models/worldpay/worldpay-form-config.interface';
import { formatWorldpayPaymentRedirectRequest } from '../../utils/worldpay/worldpay.utils';
import { WorldpayFormBuilder } from '../worldpay-form-builder/worldpay-form-builder.service';
import { WorldpayService } from '../worldpay/worldpay.service';

declare global {
  interface Window {
    WPCL: {
      Library: any;
    };
  }
}

export interface ViewModel {
  cmsData: CmsCardPage;
  form: FormGroup<WorldpayForm>;
  formValue: WorldpayFormValue;
  iframeVisible: boolean;
  loading: boolean;
  purchasing: boolean;
  selectedQuotes: QuoteDetails[];
}

export interface ComponentState {
  customerDetails?: CustomerDetails;
  firstSelectedQuote?: QuoteDetails;
  form?: FormGroup<WorldpayForm>;
  iframeVisible: boolean;
  loading: boolean;
  purchasing: boolean;
  redirectResponse?: WorldpayRedirectResponse;
  result?: WorldpayResult;
}

@Injectable()
export class WorldpayFormComponentService extends ComponentStore<ComponentState> {
  private readonly instance: WorldpayInstance = new window.WPCL.Library();

  readonly customerDetails$: Observable<CustomerDetails> = this.select(
    (state: ComponentState) => state.customerDetails
  ).pipe(filterNullUndefined());

  private readonly formConfig$: Observable<WorldpayFormConfig> = combineLatest({
    customerDetails: this.customerDetails$,
    termsAndConditionsFeatureEnabled: of(
      this.checkoutConfig.features.checkoutTermsAndConditions
    ),
  });

  private readonly form$: Observable<FormGroup<WorldpayForm>> = this.select(
    (state: ComponentState) => state.form
  ).pipe(filterNullUndefined());

  private readonly formValue$: Observable<WorldpayFormValue> = this.form$.pipe(
    formValue<WorldpayFormValue>()
  );

  readonly firstSelectedQuote$: Observable<QuoteDetails> = this.select(
    (state: ComponentState) => state.firstSelectedQuote
  ).pipe(filterNullUndefined());

  private readonly iframeVisible$: Observable<boolean> = this.select(
    (state: ComponentState) => state.iframeVisible
  );

  readonly loading$: Observable<boolean> = this.select(
    (state: ComponentState) => state.loading
  );

  private readonly purchasing$: Observable<boolean> = this.select(
    (state: ComponentState) => state.purchasing
  );

  readonly redirectResponse$: Observable<WorldpayRedirectResponse> =
    this.select((state: ComponentState) => state.redirectResponse).pipe(
      filterNullUndefined()
    );

  readonly result$: Observable<WorldpayResult> = this.select(
    (state: ComponentState) => state.result
  ).pipe(filterNullUndefined());

  readonly resultCancelled$: Observable<WorldpayResult> = this.result$.pipe(
    filter(
      (result: WorldpayResult) =>
        result.order.status === WorldpayStatus.Cancelled
    )
  );

  readonly resultSuccess$: Observable<WorldpayResult> = this.result$.pipe(
    filter(
      (result: WorldpayResult) => result.order.status === WorldpayStatus.Success
    )
  );

  readonly vm$: Observable<ViewModel> = combineLatest({
    cmsData: this.cmsContainer.content$,
    form: this.form$,
    formValue: this.formValue$,
    iframeVisible: this.iframeVisible$,
    loading: this.loading$,
    purchasing: this.purchasing$,
    selectedQuotes: this.basketFacade.selectedQuotes$,
  });

  private readonly updateCustomerDetails = this.updater(
    (state: ComponentState, customerDetails?: CustomerDetails) => ({
      ...state,
      customerDetails,
    })
  )(this.checkoutFacade.customerDetails$);

  private readonly updateForm = this.updater(
    (state: ComponentState, form: FormGroup<WorldpayForm>) => ({
      ...state,
      form,
    })
  );

  private readonly updateFirstSelectedQuote = this.updater(
    (state: ComponentState, firstSelectedQuote?: QuoteDetails) => ({
      ...state,
      firstSelectedQuote,
    })
  )(this.basketFacade.firstSelectedQuote$);

  private readonly updateIframeVisible = this.updater(
    (state: ComponentState, iframeVisible: boolean) => ({
      ...state,
      iframeVisible,
    })
  );

  private readonly updateLoading = this.updater(
    (state: ComponentState, loading: boolean) => ({
      ...state,
      loading,
    })
  );

  private readonly updatePurchasing = this.updater(
    (state: ComponentState, purchasing: boolean) => ({
      ...state,
      purchasing,
    })
  )(this.checkoutFacade.purchasing$);

  private readonly updateRedirectResponse = this.updater(
    (state: ComponentState, redirectResponse?: WorldpayRedirectResponse) => ({
      ...state,
      redirectResponse,
    })
  );

  private readonly updateResult = this.updater(
    (state: ComponentState, result: WorldpayResult) => ({
      ...state,
      result,
    })
  );

  constructor(
    @Inject(CHECKOUT_CONFIG) private readonly checkoutConfig: CheckoutConfig,
    @Inject(WORLDPAY_CONFIG) private readonly worldpayConfig: WorldpayConfig,
    private readonly basketFacade: BasketFacade,
    private readonly checkoutFacade: CheckoutFacade,
    private readonly cmsContainer: CmsContainer<CmsCardPage>,
    private readonly router: Router,
    private readonly worldpayFormBuilder: WorldpayFormBuilder,
    private readonly worldpayService: WorldpayService
  ) {
    super({
      iframeVisible: false,
      loading: false,
      purchasing: false,
    });
  }

  readonly buildForm = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      mergeMap(() => this.formConfig$),
      first(),
      map((config: WorldpayFormConfig) =>
        this.worldpayFormBuilder.buildWorldpayForm(config)
      ),
      tap((form) => this.updateForm(form))
    )
  );

  private buildWorldpayForm = this.effect(
    (trigger$: Observable<WorldpayRedirectResponse>) =>
      trigger$.pipe(
        tap((redirectResponse: WorldpayRedirectResponse) =>
          this.setup(redirectResponse)
        )
      )
  )(this.redirectResponse$);

  readonly handleIframeCancel = this.effect(
    (trigger$: Observable<WorldpayResult>) =>
      trigger$.pipe(
        mapToLatestFrom(
          this.basketFacade.selectedQuotesHaveMultipleAvailablePaymentTypes$
        ),
        tap((selectedQuotesHaveMultipleAvailablePaymentTypes: boolean) =>
          selectedQuotesHaveMultipleAvailablePaymentTypes
            ? this.router.navigateByUrl(CheckoutRoutes.PaymentMethod)
            : this.router.navigateByUrl(CheckoutRoutes.PersonalDetails)
        )
      )
  )(this.resultCancelled$);

  readonly onFormSubmit = this.effect(
    (trigger$: Observable<WorldpayFormValue>) =>
      trigger$.pipe(
        tap(() => this.updateLoading(true)),
        withLatestFrom(this.customerDetails$, this.firstSelectedQuote$),
        map(([formValue, customerDetails, firstSelectedQuote]) =>
          formatWorldpayPaymentRedirectRequest({
            billingAddress: {
              ...formValue.billingAddress,
              countryCode: customerDetails.address.countryCode,
            },
            config: this.worldpayConfig,
            customerDetails,
            firstSelectedQuote,
          })
        ),
        filterNullUndefined(),
        switchMap((redirectRequest: WorldpayRedirectRequest) =>
          this.worldpayService.redirect(redirectRequest)
        ),
        tap((redirectResponse: WorldpayRedirectResponse) =>
          this.updateRedirectResponse(redirectResponse)
        )
      )
  );

  private readonly updateDocumentSendMethod = this.effect(
    (trigger$: Observable<boolean | undefined>) =>
      trigger$.pipe(
        filterNullUndefined(),
        tap((paperlessPreference: boolean) =>
          this.checkoutFacade.updateDocumentSendMethod(
            paperlessPreference
              ? DocumentSendMethod.Both
              : DocumentSendMethod.Email
          )
        )
      )
  )(
    this.form$.pipe(
      controlValue<boolean>(WorldpayFormField.PaperlessPreference)
    )
  );

  destroy(): void {
    this.instance.destroy();
  }

  private setup(redirectResponse: WorldpayRedirectResponse): void {
    this.instance.setup({
      accessibility: true,
      customisation: this.worldpayConfig.customisation,
      debug: false,
      flowCallback: () => {
        this.updateLoading(false);
        this.updateIframeVisible(true);
      },
      iframeHelperURL: this.worldpayConfig.iframeHelperURL,
      inject: 'immediate',
      resultCallback: (result: WorldpayResult) => this.updateResult(result),
      target: 'worldpay-form-iframe',
      type: WorldpayIntegrationType.Iframe,
      url: redirectResponse.url,
    });
  }
}
